import { useState, useEffect } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Banknote, Plus, Edit, Trash2, Upload, Download, FileText, IndianRupee, Wallet, TrendingUp, Search, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import LiquidityWidget from '../components/LiquidityWidget';
import BalanceLedger from '../components/BalanceLedger';
import BankTransactions from '../components/BankTransactions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Bank = () => {
  const { user, hasTabAccess } = useAuth();
  const location = useLocation();
  // When the Accounting tab deep-links here with ?highlight=<payment_id>, we
  // pin the row to the top of the list and pulse it briefly so the
  // accountant can find it instantly.
  const highlightId = new URLSearchParams(location.search).get('highlight') || '';
  const [payments, setPayments] = useState([]);
  // Scroll the highlighted payment into view once it lands in the list.
  useEffect(() => {
    if (!highlightId || payments.length === 0) return;
    const el = document.getElementById(`payment-${highlightId}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [highlightId, payments]);
  const [vendors, setVendors] = useState([]);
  const [partyInvoices, setPartyInvoices] = useState([]);
  // View allocations modal — shows the invoices a payment was set off against
  const [viewAllocPayment, setViewAllocPayment] = useState(null);
  const [allocDetail, setAllocDetail] = useState(null);
  const [allocLoading, setAllocLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  // 'all' | 'unallocated' | 'allocated' — surfaces the chunk of bank
  // payments that still need to be set off against invoices.
  const [allocationFilter, setAllocationFilter] = useState('all');
  const [defaultTournament, setDefaultTournament] = useState('');
  
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    narration: '',
    amount: '',
    party_name: '',
    party_id: '',
    payment_mode: 'Bank Transfer',
    reference_number: '',
    allocated_invoices: []
  });

  // Filter payments based on search term, date range and allocation status
  const filteredPayments = payments.filter(payment => {
    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const matchesSearch = (
        payment.party_name?.toLowerCase().includes(term) ||
        payment.narration?.toLowerCase().includes(term) ||
        payment.payment_mode?.toLowerCase().includes(term) ||
        payment.reference_number?.toLowerCase().includes(term)
      );
      if (!matchesSearch) return false;
    }
    
    // Date range filter
    if (dateFrom || dateTo) {
      const paymentDate = new Date(payment.date);
      if (dateFrom && paymentDate < new Date(dateFrom)) return false;
      if (dateTo && paymentDate > new Date(dateTo)) return false;
    }

    // Allocation filter — a payment is "unallocated" if any rupee of its
    // amount hasn't been mapped to an invoice yet.
    if (allocationFilter !== 'all') {
      const allocSum = (payment.allocated_invoices || []).reduce(
        (s, a) => s + (a.allocated_amount || 0), 0
      );
      const unalloc = (payment.amount || 0) - allocSum;
      if (allocationFilter === 'unallocated' && unalloc <= 0.01) return false;
      if (allocationFilter === 'allocated' && unalloc > 0.01) return false;
    }

    return true;
  });

  useEffect(() => {
    fetchPayments();
    fetchVendors();
    // Fetch the current default tournament so the invoice picker can scope to it
    (async () => {
      try {
        const res = await axios.get(`${API}/tournaments`, { withCredentials: true });
        const list = res?.data?.tournaments || [];
        const def =
          list.find((t) => t.is_default && t.is_active) ||
          list.find((t) => t.is_default) ||
          list.find((t) => t.is_active);
        if (def?.name) setDefaultTournament(def.name);
      } catch (e) {
        console.warn('Bank: tournament list fetch failed:', e?.response?.data || e?.message);
      }
    })();
  }, []);

  useEffect(() => {
    if (formData.party_id) {
      // When editing, include the current payment's existing allocations so
      // the already-allocated invoices surface in the picker with green ticks.
      fetchPartyInvoices(formData.party_id, editingPayment?.id);
    } else {
      setPartyInvoices([]);
    }
    // Re-fetch when the default tournament resolves so the picker scopes correctly
  }, [formData.party_id, defaultTournament, editingPayment?.id]);

  const fetchPayments = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/payments`, { withCredentials: true });
      setPayments(response.data);
    } catch (error) {
      toast.error('Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  const fetchVendors = async () => {
    try {
      const response = await axios.get(`${API}/vendors`, { withCredentials: true });
      setVendors(response.data);
    } catch (error) {
      console.error('Failed to load vendors');
    }
  };

  const fetchPartyInvoices = async (partyId, includePaymentId) => {
    try {
      const params = new URLSearchParams();
      if (defaultTournament) params.append('tournament', defaultTournament);
      if (includePaymentId) params.append('include_payment_id', includePaymentId);
      const url = `${API}/invoices/by-party/${partyId}${
        params.toString() ? `?${params.toString()}` : ''
      }`;
      const response = await axios.get(url, { withCredentials: true });
      setPartyInvoices(response.data);
    } catch (error) {
      console.error('Failed to load party invoices');
      setPartyInvoices([]);
    }
  };

  const openViewAllocations = async (payment) => {
    setViewAllocPayment(payment);
    setAllocDetail(null);
    setAllocLoading(true);
    try {
      const res = await axios.get(
        `${API}/payments/${payment.id}/allocations-detail`,
        { withCredentials: true }
      );
      setAllocDetail(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load allocations');
      setViewAllocPayment(null);
    } finally {
      setAllocLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      date: new Date().toISOString().split('T')[0],
      narration: '',
      amount: '',
      party_name: '',
      party_id: '',
      payment_mode: 'Bank Transfer',
      reference_number: '',
      allocated_invoices: []
    });
    setEditingPayment(null);
    setPartyInvoices([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const payload = {
        ...formData,
        amount: parseFloat(formData.amount),
        date: new Date(formData.date).toISOString()
      };

      if (editingPayment) {
        await axios.put(`${API}/payments/${editingPayment.id}`, payload, {
          withCredentials: true,
        });
        toast.success('Payment updated successfully');
      } else {
        await axios.post(`${API}/payments`, payload, {
          withCredentials: true,
        });
        toast.success('Payment created successfully');
      }

      setDialogOpen(false);
      resetForm();
      fetchPayments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save payment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (payment) => {
    setEditingPayment(payment);
    setFormData({
      date: new Date(payment.date).toISOString().split('T')[0],
      narration: payment.narration,
      amount: payment.amount.toString(),
      party_name: payment.party_name,
      party_id: payment.party_id || '',
      payment_mode: payment.payment_mode || 'Bank Transfer',
      reference_number: payment.reference_number || '',
      allocated_invoices: payment.allocated_invoices || []
    });
    setDialogOpen(true);
  };

  const handleDelete = async (paymentId) => {
    if (!window.confirm('Are you sure you want to delete this payment?')) {
      return;
    }

    try {
      await axios.delete(`${API}/payments/${paymentId}`, { withCredentials: true });
      toast.success('Payment deleted');
      fetchPayments();
    } catch (error) {
      toast.error('Failed to delete payment');
    }
  };

  const openNewDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const handleVendorChange = (vendorId) => {
    const vendor = vendors.find(v => v.id === vendorId);
    setFormData({
      ...formData,
      party_id: vendorId,
      party_name: vendor ? vendor.name : '',
      allocated_invoices: []
    });
  };

  const handleInvoiceAllocation = (invoiceId, checked) => {
    const invoice = partyInvoices.find(inv => inv.id === invoiceId);
    if (!invoice) return;

    let newAllocations = [...formData.allocated_invoices];

    if (checked) {
      // Auto-fill allocation = min(invoice's outstanding, remaining bank amount).
      // This prevents the common bug where ticking a single invoice marks it
      // "fully paid" even when the actual bank transfer was less.
      const outstanding = invoice.outstanding_amount || 0;
      const bankAmount = parseFloat(formData.amount || 0) || 0;
      const alreadyAllocated = newAllocations.reduce(
        (s, a) => s + (a.allocated_amount || 0),
        0
      );
      const remainingBank = Math.max(0, bankAmount - alreadyAllocated);
      const fill = bankAmount > 0 ? Math.min(outstanding, remainingBank) : outstanding;
      newAllocations.push({
        invoice_id: invoiceId,
        allocated_amount: Math.round(fill * 100) / 100,
      });
    } else {
      // Remove allocation
      newAllocations = newAllocations.filter(a => a.invoice_id !== invoiceId);
    }

    setFormData({ ...formData, allocated_invoices: newAllocations });
  };

  const handleAllocationAmountChange = (invoiceId, amount) => {
    const invoice = partyInvoices.find((inv) => inv.id === invoiceId);
    const max = invoice ? (invoice.outstanding_amount || 0) : Infinity;
    let val = parseFloat(amount) || 0;
    if (val < 0) val = 0;
    // Clamp at the invoice's outstanding so the user cannot allocate more than payable.
    if (val > max) val = max;
    const newAllocations = formData.allocated_invoices.map((a) =>
      a.invoice_id === invoiceId ? { ...a, allocated_amount: Math.round(val * 100) / 100 } : a
    );
    setFormData({ ...formData, allocated_invoices: newAllocations });
  };

  const getTotalAllocated = () => {
    return formData.allocated_invoices.reduce((sum, a) => sum + (a.allocated_amount || 0), 0);
  };

  // Calculate summary metrics
  const summaryMetrics = {
    totalPayments: payments.length,
    totalAmount: payments.reduce((sum, p) => sum + (p.amount || 0), 0),
    totalAllocated: payments.reduce((sum, p) => {
      const allocated = p.allocated_invoices?.reduce((s, a) => s + (a.allocated_amount || 0), 0) || 0;
      return sum + allocated;
    }, 0),
    totalUnallocated: payments.reduce((sum, p) => {
      const allocated = p.allocated_invoices?.reduce((s, a) => s + (a.allocated_amount || 0), 0) || 0;
      return sum + ((p.amount || 0) - allocated);
    }, 0),
  };

  // Download CSV template
  const downloadTemplate = () => {
    const csvContent = `date,party_name,amount,narration,payment_mode,reference_number
2024-01-15,Vendor ABC,50000,Payment for Invoice INV-001,Bank Transfer,TXN123456
2024-01-16,Vendor XYZ,75000,Payment for services,Cheque,CHQ789012`;
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'bank_payments_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Template downloaded');
  };

  // Handle CSV file import
  const handleFileImport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImporting(true);
    const reader = new FileReader();
    
    reader.onload = async (e) => {
      try {
        const text = e.target?.result;
        const lines = text.split('\n').filter(line => line.trim());
        const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
        
        const requiredHeaders = ['date', 'party_name', 'amount', 'narration'];
        const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));
        
        if (missingHeaders.length > 0) {
          toast.error(`Missing required columns: ${missingHeaders.join(', ')}`);
          setImporting(false);
          return;
        }

        const dataRows = lines.slice(1);
        let successCount = 0;
        let errorCount = 0;

        for (const row of dataRows) {
          if (!row.trim()) continue;
          
          const values = row.split(',').map(v => v.trim());
          const rowData = {};
          headers.forEach((header, index) => {
            rowData[header] = values[index] || '';
          });

          // Find vendor by name
          const vendor = vendors.find(v => 
            v.name.toLowerCase() === rowData.party_name.toLowerCase()
          );

          const paymentData = {
            date: new Date(rowData.date).toISOString(),
            party_name: rowData.party_name,
            party_id: vendor?.id || '',
            amount: parseFloat(rowData.amount) || 0,
            narration: rowData.narration || '',
            payment_mode: rowData.payment_mode || 'Bank Transfer',
            reference_number: rowData.reference_number || '',
            allocated_invoices: []
          };

          try {
            await axios.post(`${API}/payments`, paymentData, { withCredentials: true });
            successCount++;
          } catch (err) {
            errorCount++;
            console.error('Failed to import row:', err);
          }
        }

        if (successCount > 0) {
          toast.success(`Successfully imported ${successCount} payments`);
          fetchPayments();
        }
        if (errorCount > 0) {
          toast.error(`Failed to import ${errorCount} payments`);
        }
        
        setImportDialogOpen(false);
      } catch (error) {
        toast.error('Failed to parse CSV file');
        console.error(error);
      } finally {
        setImporting(false);
        event.target.value = '';
      }
    };

    reader.readAsText(file);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div data-testid="bank-page" className="space-y-6">
      <div>
        <h1 className="text-4xl font-heading font-bold tracking-tight">Bank</h1>
        <p className="text-muted-foreground mt-1">Balances, fixed deposits and payments</p>
      </div>

      {/* Liquidity snapshot. Previously lived on the Dashboard but was moved
          here (Feb 2026) because it duplicated the Cash Flow → Actual tile
          there. Scoped to the default tournament when one is set so the
          composition numbers tie out to the rest of the Bank workflows. */}
      <LiquidityWidget tournament={defaultTournament || null} />

      <Tabs defaultValue="payments" className="w-full">
        <TabsList className="grid grid-cols-4 max-w-3xl">
          <TabsTrigger value="payments" data-testid="tab-payments">Payments</TabsTrigger>
          <TabsTrigger value="bank-balances" data-testid="tab-bank-balances">Bank Balances</TabsTrigger>
          <TabsTrigger value="fd-balances" data-testid="tab-fd-balances">Fixed Deposits</TabsTrigger>
          <TabsTrigger value="bank-transactions" data-testid="tab-bank-transactions">Bank Transactions</TabsTrigger>
        </TabsList>
        <TabsContent value="bank-balances" className="mt-4">
          <BalanceLedger
            type="BANK"
            title="Bank Balances"
            description="Record daily closing balance per bank account. Dashboard liquidity uses the latest entry on or before the selected date."
          />
        </TabsContent>
        <TabsContent value="fd-balances" className="mt-4">
          <BalanceLedger
            type="FD"
            title="Fixed Deposits"
            description="Record the current FD balance per account as it changes (renewals, new FDs, withdrawals)."
          />
        </TabsContent>
        <TabsContent value="bank-transactions" className="mt-4">
          <BankTransactions />
        </TabsContent>
        <TabsContent value="payments" className="mt-4 space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-heading font-semibold tracking-tight">Bank Payments</h2>
          <p className="text-muted-foreground mt-1">Track payments and set off against invoices</p>
        </div>
        <div className="flex gap-3 flex-wrap justify-end">
          {/* Search Input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search payments..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 w-48"
              data-testid="search-payment-input"
            />
          </div>
          {/* Date Filters */}
          <div className="flex gap-2 items-center">
            <Input
              type="date"
              placeholder="From"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="w-36"
              data-testid="date-from-input"
            />
            <span className="text-muted-foreground">to</span>
            <Input
              type="date"
              placeholder="To"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="w-36"
              data-testid="date-to-input"
            />
          </div>
          {hasTabAccess('bank') && (
            <>
              {/* Import Dialog */}
              <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" data-testid="import-payments-button">
                    <Upload className="h-4 w-4 mr-2" />
                    Import CSV
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle className="font-heading">Import Bank Payments</DialogTitle>
                    <DialogDescription>
                      Upload a CSV file to bulk import bank payments
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="p-4 bg-muted/30 rounded-lg">
                      <h4 className="font-semibold text-sm mb-2">CSV Format Requirements:</h4>
                      <ul className="text-sm text-muted-foreground space-y-1">
                        <li>• <strong>date</strong> (required): YYYY-MM-DD format</li>
                        <li>• <strong>party_name</strong> (required): Vendor name</li>
                        <li>• <strong>amount</strong> (required): Payment amount</li>
                        <li>• <strong>narration</strong> (required): Description</li>
                        <li>• <strong>payment_mode</strong> (optional): Bank Transfer, Cheque, Cash, Online</li>
                        <li>• <strong>reference_number</strong> (optional): Transaction/Cheque number</li>
                      </ul>
                    </div>
                    <Button variant="outline" onClick={downloadTemplate} className="w-full">
                      <Download className="h-4 w-4 mr-2" />
                      Download Template
                    </Button>
                    <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
                      <input
                        type="file"
                      accept=".csv"
                      onChange={handleFileImport}
                      className="hidden"
                      id="csv-upload"
                      disabled={importing}
                    />
                    <label htmlFor="csv-upload" className="cursor-pointer">
                      <Upload className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                      <p className="text-sm text-muted-foreground">
                        {importing ? 'Importing...' : 'Click to upload CSV file'}
                      </p>
                    </label>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            {/* Add Payment Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-payment-button" data-write-only onClick={openNewDialog}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Payment
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading">
                  {editingPayment ? 'Edit Payment' : 'Create Payment'}
                </DialogTitle>
                <DialogDescription>
                  Record bank payment and allocate to invoices
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="date">Date *</Label>
                    <Input
                      id="date"
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({...formData, date: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="amount">Amount (₹) *</Label>
                    <Input
                      id="amount"
                      type="number"
                      step="0.01"
                      value={formData.amount}
                      onChange={(e) => setFormData({...formData, amount: e.target.value})}
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="party_id">Party (Vendor) *</Label>
                  <Select value={formData.party_id} onValueChange={handleVendorChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select vendor" />
                    </SelectTrigger>
                    <SelectContent>
                      {vendors.map((vendor) => (
                        <SelectItem key={vendor.id} value={vendor.id}>
                          {vendor.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="narration">Narration *</Label>
                  <Textarea
                    id="narration"
                    value={formData.narration}
                    onChange={(e) => setFormData({...formData, narration: e.target.value})}
                    rows={2}
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="payment_mode">Payment Mode</Label>
                    <Select value={formData.payment_mode} onValueChange={(value) => setFormData({...formData, payment_mode: value})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                        <SelectItem value="Cheque">Cheque</SelectItem>
                        <SelectItem value="Cash">Cash</SelectItem>
                        <SelectItem value="Online">Online</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="reference_number">Reference Number</Label>
                    <Input
                      id="reference_number"
                      value={formData.reference_number}
                      onChange={(e) => setFormData({...formData, reference_number: e.target.value})}
                      placeholder="Transaction ID / Cheque No"
                    />
                  </div>
                </div>

                {/* Invoice Allocation */}
                {formData.party_id && partyInvoices.length > 0 && (
                  <div className="border-t pt-4">
                    <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
                      <Label className="text-base font-semibold block">Set Off Against Invoices</Label>
                      {defaultTournament && (
                        <span className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground bg-muted px-2 py-1 rounded">
                          Tournament: {defaultTournament}
                        </span>
                      )}
                    </div>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {partyInvoices.map((invoice) => {
                        const allocation = formData.allocated_invoices.find(a => a.invoice_id === invoice.id);
                        const isAllocated = !!allocation;
                        
                        return (
                          <div key={invoice.id} className="flex items-center gap-3 p-3 bg-muted/30 rounded border">
                            <Checkbox
                              checked={isAllocated}
                              onCheckedChange={(checked) => handleInvoiceAllocation(invoice.id, checked)}
                            />
                            <div className="flex-1">
                              <p className="text-sm font-medium">{invoice.invoice_number}</p>
                              <p className="text-xs text-muted-foreground">
                                PO: {invoice.po_number} • Outstanding: ₹{invoice.outstanding_amount?.toLocaleString('en-IN')}
                              </p>
                            </div>
                            {isAllocated && (
                              <div className="flex flex-col items-end gap-1">
                                <Input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  max={invoice.outstanding_amount || 0}
                                  value={allocation.allocated_amount}
                                  onChange={(e) => handleAllocationAmountChange(invoice.id, e.target.value)}
                                  className="w-32"
                                  placeholder="Amount"
                                  data-testid={`allocation-amount-${invoice.id}`}
                                />
                                <span className="text-[10px] text-muted-foreground">
                                  max ₹{(invoice.outstanding_amount || 0).toLocaleString('en-IN')}
                                </span>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    <div className="mt-3 p-3 bg-primary/10 rounded">
                      <div className="flex justify-between text-sm">
                        <span>Payment Amount:</span>
                        <span className="font-bold">₹{parseFloat(formData.amount || 0).toLocaleString('en-IN')}</span>
                      </div>
                      <div className="flex justify-between text-sm mt-1">
                        <span>Total Allocated:</span>
                        <span className={`font-bold ${getTotalAllocated() > parseFloat(formData.amount || 0) + 0.01 ? 'text-destructive' : ''}`}>
                          ₹{getTotalAllocated().toLocaleString('en-IN')}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm mt-1">
                        <span>Unallocated:</span>
                        <span className={`font-bold ${parseFloat(formData.amount || 0) - getTotalAllocated() < 0 ? 'text-destructive' : ''}`}>
                          ₹{(parseFloat(formData.amount || 0) - getTotalAllocated()).toLocaleString('en-IN')}
                        </span>
                      </div>
                      {getTotalAllocated() > parseFloat(formData.amount || 0) + 0.01 && (
                        <div className="mt-2 text-xs text-destructive font-medium">
                          ⚠ Allocation exceeds bank payment. Reduce per-invoice amounts.
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {formData.party_id && partyInvoices.length === 0 && (
                  <div
                    className="border-t pt-4 text-sm text-muted-foreground"
                    data-testid="no-pending-invoices-msg"
                  >
                    No pending invoices for this vendor
                    {defaultTournament ? ` in ${defaultTournament}` : ''}.
                    Switch the default tournament from <strong>Tournament Management</strong> to
                    allocate to invoices from a different tournament.
                  </div>
                )}

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={submitting}>
                    {submitting ? 'Saving...' : editingPayment ? 'Update Payment' : 'Create Payment'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
            </>
          )}
        </div>
      </div>

      {/* Summary Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-border bg-gradient-to-br from-blue-500/10 to-blue-600/5">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Payments</p>
                <p className="text-3xl font-bold">{summaryMetrics.totalPayments}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-blue-500/20 flex items-center justify-center">
                <FileText className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-border bg-gradient-to-br from-emerald-500/10 to-emerald-600/5">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Amount</p>
                <p className="text-2xl font-bold text-emerald-600">
                  ₹{summaryMetrics.totalAmount.toLocaleString('en-IN')}
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-emerald-500/20 flex items-center justify-center">
                <IndianRupee className="h-6 w-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-border bg-gradient-to-br from-green-500/10 to-green-600/5">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Allocated to Invoices</p>
                <p className="text-2xl font-bold text-green-600">
                  ₹{summaryMetrics.totalAllocated.toLocaleString('en-IN')}
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-green-500/20 flex items-center justify-center">
                <Wallet className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card
          className={`border-border bg-gradient-to-br from-orange-500/10 to-orange-600/5 cursor-pointer transition ring-offset-2 ${
            allocationFilter === 'unallocated' ? 'ring-2 ring-orange-500' : 'hover:shadow-md'
          }`}
          onClick={() =>
            setAllocationFilter(allocationFilter === 'unallocated' ? 'all' : 'unallocated')
          }
          data-testid="unallocated-card"
          title="Click to filter — show only payments with an unallocated component"
        >
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  Unallocated
                  {allocationFilter === 'unallocated' && (
                    <span className="text-[10px] uppercase tracking-wider text-orange-700 bg-orange-100 px-1.5 py-0.5 rounded">
                      filtered
                    </span>
                  )}
                </p>
                <p className="text-2xl font-bold text-orange-600">
                  ₹{summaryMetrics.totalUnallocated.toLocaleString('en-IN')}
                </p>
                <p className="text-[11px] text-muted-foreground mt-1">
                  {allocationFilter === 'unallocated' ? 'Click to clear filter' : 'Click to see only unallocated transactions'}
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-orange-500/20 flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border-border">
        <CardHeader className="border-b border-border/50">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <CardTitle className="font-heading">All Payments</CardTitle>
              <CardDescription>
                {searchTerm || allocationFilter !== 'all' || dateFrom || dateTo
                  ? `Showing ${filteredPayments.length} of ${payments.length} payments`
                  : `Total: ${payments.length} payments`}
                {allocationFilter !== 'all' && (
                  <span className="ml-2 text-xs text-orange-700">
                    · {allocationFilter === 'unallocated' ? 'Unallocated only' : 'Fully allocated only'}
                  </span>
                )}
              </CardDescription>
            </div>
            {allocationFilter !== 'all' && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setAllocationFilter('all')}
                data-testid="clear-allocation-filter"
              >
                Clear allocation filter
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          {filteredPayments.length === 0 ? (
            <div className="text-center py-12">
              <Banknote className="h-12 w-12 text-muted-foreground mx-auto mb-4" strokeWidth={1.5} />
              <p className="text-muted-foreground">{searchTerm ? 'No matching payments found' : 'No payments found'}</p>
              <p className="text-sm text-muted-foreground mt-1">
                {searchTerm ? 'Try a different search term' : 'Create a payment to track bank transactions'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-muted/50 text-muted-foreground uppercase text-xs tracking-wider font-semibold">
                    <th className="text-left py-3 px-4">Date</th>
                    <th className="text-left py-3 px-4">Party Name</th>
                    <th className="text-left py-3 px-4">Narration</th>
                    <th className="text-right py-3 px-4">Amount</th>
                    <th className="text-right py-3 px-4">Unallocated</th>
                    <th className="text-left py-3 px-4">Payment Mode</th>
                    <th className="text-left py-3 px-4">Reference</th>
                    <th className="text-center py-3 px-4">Invoices</th>
                    <th className="text-center py-3 px-4">Bank Txn</th>
                    {hasTabAccess('bank') && <th className="text-center py-3 px-4">Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {filteredPayments.map((payment) => {
                    const allocSum = (payment.allocated_invoices || []).reduce(
                      (s, a) => s + (a.allocated_amount || 0), 0
                    );
                    const unalloc = Math.max(0, (payment.amount || 0) - allocSum);
                    const hasUnalloc = unalloc > 0.01;
                    const fullyUnalloc = allocSum < 0.01 && (payment.amount || 0) > 0;
                    return (
                    <tr
                      key={payment.id}
                      id={`payment-${payment.id}`}
                      data-testid={`payment-row-${payment.id}`}
                      className={`border-b border-border transition-colors ${
                        highlightId === payment.id
                          ? 'bg-amber-50 ring-2 ring-amber-300 animate-pulse'
                          : hasUnalloc
                          ? 'bg-orange-50/60 hover:bg-orange-50'
                          : 'hover:bg-muted/30'
                      }`}
                    >
                      <td className="py-3 px-4 text-sm">
                        {new Date(payment.date).toLocaleDateString()}
                      </td>
                      <td className="py-3 px-4 font-medium">{payment.party_name}</td>
                      <td className="py-3 px-4 text-sm">{payment.narration}</td>
                      <td className="py-3 px-4 text-right font-bold text-primary">
                        ₹{payment.amount?.toLocaleString('en-IN')}
                      </td>
                      <td
                        className="py-3 px-4 text-right text-sm font-mono"
                        data-testid={`unalloc-${payment.id}`}
                      >
                        {hasUnalloc ? (
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold ${
                              fullyUnalloc
                                ? 'bg-orange-100 text-orange-800'
                                : 'bg-amber-100 text-amber-800'
                            }`}
                            title={fullyUnalloc ? 'No invoice mapped' : 'Partially allocated'}
                          >
                            ₹{unalloc.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                          </span>
                        ) : (
                          <span className="text-emerald-700 text-xs">Fully set off</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-sm">{payment.payment_mode}</td>
                      <td className="py-3 px-4 text-sm font-mono">
                        {payment.reference_number || '-'}
                      </td>
                      <td className="py-3 px-4 text-center text-sm">
                        {payment.allocated_invoices?.length || 0} allocated
                      </td>
                      <td className="py-3 px-4 text-center text-sm">
                        {payment.bank_txn_id ? (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold bg-emerald-100 text-emerald-800"
                            title="This payment is linked to an imported bank statement transaction"
                            data-testid={`bank-txn-tag-${payment.id}`}
                          >
                            <span className="h-1.5 w-1.5 rounded-full bg-emerald-600" />
                            Tagged
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                      {hasTabAccess('bank') && (
                        <td className="py-3 px-4 text-center">
                          <div className="flex gap-2 justify-center">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openViewAllocations(payment)}
                              data-testid={`view-allocations-${payment.id}`}
                              title="View invoices this payment is allocated to"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleEdit(payment)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDelete(payment.id)}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </td>
                      )}
                    </tr>
                  );})}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
        </TabsContent>
      </Tabs>

      {/* View Allocations modal */}
      <Dialog open={!!viewAllocPayment} onOpenChange={(o) => !o && setViewAllocPayment(null)}>
        <DialogContent className="max-w-3xl" data-testid="view-allocations-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Allocations for this payment</DialogTitle>
            <DialogDescription>
              Invoices that this bank payment was set off against.
            </DialogDescription>
          </DialogHeader>

          {allocLoading && <p className="text-sm text-muted-foreground py-6">Loading…</p>}

          {!allocLoading && allocDetail && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm bg-muted/40 rounded-lg p-3">
                <div>
                  <span className="text-muted-foreground">Vendor:</span>{' '}
                  <span className="font-medium">{allocDetail.party_name || '—'}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Payment date:</span>{' '}
                  <span className="font-medium">
                    {allocDetail.payment_date ? new Date(allocDetail.payment_date).toLocaleDateString() : '—'}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground">Amount:</span>{' '}
                  <span className="font-bold text-primary">₹{(allocDetail.payment_amount || 0).toLocaleString('en-IN')}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Unallocated:</span>{' '}
                  <span className={`font-medium ${allocDetail.unallocated > 0.01 ? 'text-amber-700' : 'text-emerald-700'}`}>
                    ₹{(allocDetail.unallocated || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                  </span>
                </div>
              </div>

              {allocDetail.allocations.length === 0 ? (
                <p className="text-sm text-muted-foreground italic py-6 text-center">
                  This payment is not set off against any invoice.
                </p>
              ) : (
                <div className="overflow-x-auto border rounded-md">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50 text-muted-foreground uppercase text-xs tracking-wider font-semibold">
                      <tr>
                        <th className="text-left py-2 px-3">Invoice</th>
                        <th className="text-left py-2 px-3">PO</th>
                        <th className="text-left py-2 px-3">Dept · Head</th>
                        <th className="text-right py-2 px-3">Invoice Total</th>
                        <th className="text-right py-2 px-3">Allocated</th>
                        <th className="text-right py-2 px-3">Outstanding</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allocDetail.allocations.map((a) => (
                        <tr key={a.invoice_id} className="border-t border-border">
                          <td className="py-2 px-3 font-medium">{a.invoice_number}</td>
                          <td className="py-2 px-3 text-xs text-muted-foreground">{a.po_number || '—'}</td>
                          <td className="py-2 px-3 text-xs text-muted-foreground">
                            {[a.department, a.budget_head].filter(Boolean).join(' · ') || '—'}
                          </td>
                          <td className="py-2 px-3 text-right">
                            ₹{(a.net_payable || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                          </td>
                          <td className="py-2 px-3 text-right font-semibold text-primary">
                            ₹{(a.allocated_amount || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                          </td>
                          <td className="py-2 px-3 text-right">
                            <span className={a.outstanding_amount > 0.01 ? 'text-amber-700' : 'text-emerald-700'}>
                              ₹{(a.outstanding_amount || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-muted/30 border-t font-semibold">
                        <td colSpan="4" className="py-2 px-3 text-right">Total allocated:</td>
                        <td className="py-2 px-3 text-right text-primary">
                          ₹{(allocDetail.total_allocated || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                        </td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setViewAllocPayment(null)} data-testid="view-allocations-close">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Bank;
